// Session + scope store.
//
// Single zustand store that owns the current AuthUser + Scope. Consumers read
// it via the narrow selectors `useCurrentUser` / `useCurrentScope` to keep
// re-render surfaces small.
//
// Fetch contract:
//   - GET /auth/me → { user, scope } on 200
//   - 401 clears local state (user logged out server-side)
//   - network errors keep stale data and surface `error` for banners
//
// Переключение workspace:
//   - список доступных — GET /auth/workspaces (только активные членства);
//   - выбор хранится локально (`activeWorkspace`) и уходит в `X-Workspace-Id`;
//   - после смены кэш react-query сбрасывается: в нём данные прошлого workspace;
//   - если сервер отказал (403 not_a_member — членство отозвали), выбор
//     откатывается на прежний, чтобы приложение не осталось в мёртвом scope.

import { create } from 'zustand';

import type { AuthUser } from '@govard/auth-adapter';
import type { Scope } from '@govard/tenant-core';

import { ApiError, apiGet, apiPost } from '../api-client/index.js';
import { queryClient } from '../query/queryClient.js';
import { useSelectionStore } from '../selection/useSelectionStore.js';
import { readActiveWorkspaceId, writeActiveWorkspaceId } from './activeWorkspace.js';

export interface WorkspaceSettingsState {
  workspaceId: string;
  featureFlags: Record<string, boolean>;
}

/**
 * Billing snapshot from `GET /auth/me` (ADR-029). The backend returns it only
 * when a scope is resolved (`billing: null` otherwise), so the field is
 * optional on the wire — old responses without it must keep working.
 */
export interface BillingEntitlement {
  allowed: boolean;
  limit: number | 'unlimited';
  currentUsage?: number;
}

export interface BillingEntitlements {
  plan: string;
  isBillable: boolean;
  entitlements: Record<string, BillingEntitlement>;
}

interface MeResponse {
  user: AuthUser;
  // The backend also returns `session`, but the web shell only needs
  // user + scope. Keep the parsed shape loose.
  session?: unknown;
  scope: Scope;
  workspaceSettings: WorkspaceSettingsState;
  billing?: BillingEntitlements | null;
  // Platform role for the admin gate (admin panel P0). Optional on the wire:
  // pre-admin servers did not ship it, and a missing value must fail closed
  // (treated as a regular user).
  platformRole?: 'user' | 'admin';
}

export interface WorkspaceOption {
  id: string;
  slug: string;
  name: string;
  kind: string;
  role: string;
  isPersonal: boolean;
}

interface WorkspacesResponse {
  workspaces: WorkspaceOption[];
}

interface ScopeStoreState {
  user: AuthUser | null;
  scope: Scope | null;
  workspaceSettings: WorkspaceSettingsState | null;
  billing: BillingEntitlements | null;
  /** Platform role from /auth/me; null = unknown → treated as 'user' (fail-closed). */
  platformRole: 'user' | 'admin' | null;
  workspaces: WorkspaceOption[];
  isLoading: boolean;
  isSwitchingWorkspace: boolean;
  error: Error | null;
  fetchMe: () => Promise<void>;
  fetchWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  createWorkspace: (name: string) => Promise<WorkspaceOption>;
  clear: () => void;
}

type SetScopeState = (partial: Partial<ScopeStoreState>) => void;

/**
 * Единая точка применения ответа `/auth/me`. Сервер — источник истины о том,
 * какой workspace активен (он мог не принять наш выбор), поэтому сохраняем
 * именно резолвнутый им id: следующий запрос уйдёт с тем же явным заголовком.
 */
function applyMe(set: SetScopeState, me: MeResponse): void {
  writeActiveWorkspaceId(me.scope?.workspaceId ?? null);
  set({
    user: me.user,
    scope: me.scope,
    workspaceSettings: me.workspaceSettings,
    // Billing is optional on the wire (pre-billing servers / no scope): the
    // store normalizes missing payloads to null so consumers can fail open.
    billing: me.billing ?? null,
    // Platform role gates /admin. Missing field (pre-admin server) → null, and
    // useIsPlatformAdmin() treats null as a regular user.
    platformRole: me.platformRole ?? null,
    error: null,
  });
}

export const useScopeStore = create<ScopeStoreState>((set, get) => ({
  user: null,
  scope: null,
  workspaceSettings: null,
  billing: null,
  platformRole: null,
  workspaces: [],
  isLoading: false,
  isSwitchingWorkspace: false,
  error: null,

  fetchMe: async () => {
    if (get().isLoading) {
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const me = await apiGet<MeResponse>('/auth/me');
      applyMe(set, me);
      set({ isLoading: false, error: null });
    } catch (err) {
      let failure: unknown = err;
      // 403 при сохранённом выборе workspace: членство отозвали, пока нас не
      // было, и с этим заголовком каждый /auth/me будет падать — включая
      // повторный вход. Забываем мёртвый выбор и пробуем ещё раз: сервер сам
      // посадит сессию на доступный workspace.
      if (
        failure instanceof ApiError &&
        failure.status === 403 &&
        readActiveWorkspaceId() !== null
      ) {
        writeActiveWorkspaceId(null);
        queryClient.clear();
        try {
          const me = await apiGet<MeResponse>('/auth/me');
          applyMe(set, me);
          set({ isLoading: false, error: null });
          return;
        } catch (retryErr) {
          failure = retryErr;
        }
      }
      if (failure instanceof ApiError && failure.status === 401) {
        // Unauthorized = not logged in. Clear any stale state.
        writeActiveWorkspaceId(null);
        // В кэше — данные сессии, которой больше нет; следующему пользователю
        // этой вкладки они не принадлежат.
        queryClient.clear();
        set({
          user: null,
          scope: null,
          workspaceSettings: null,
          billing: null,
          platformRole: null,
          workspaces: [],
          isLoading: false,
          error: null,
        });
        return;
      }
      // Other errors (network, 5xx) — keep stale data, surface the error.
      const error =
        failure instanceof Error ? failure : new Error('unknown error fetching /auth/me');
      set({ isLoading: false, error });
    }
  },

  fetchWorkspaces: async () => {
    try {
      const res = await apiGet<WorkspacesResponse>('/auth/workspaces');
      set({ workspaces: Array.isArray(res.workspaces) ? res.workspaces : [] });
    } catch {
      // Не критично: без списка шапка просто покажет текущий workspace без выбора.
      set({ workspaces: [] });
    }
  },

  switchWorkspace: async (workspaceId: string) => {
    if (get().isSwitchingWorkspace || workspaceId === get().scope?.workspaceId) {
      return;
    }
    const previous = readActiveWorkspaceId();
    set({ isSwitchingWorkspace: true, error: null });
    // Запросы в полёте уже уйдут с новым заголовком, а их результаты легли бы в
    // ключи прошлого workspace — то есть в старом контексте появились бы чужие
    // данные. Поэтому сначала снимаем всё, что летит, и только потом меняем выбор.
    await queryClient.cancelQueries();
    writeActiveWorkspaceId(workspaceId);
    try {
      const me = await apiGet<MeResponse>('/auth/me');
      queryClient.clear();
      // Выделенные строки принадлежали прошлому workspace: «Заметка к выбранным»
      // иначе прицепилась бы к записям, которых в новом контексте нет.
      useSelectionStore.getState().clear();
      applyMe(set, me);
      set({ isSwitchingWorkspace: false });
    } catch (err) {
      writeActiveWorkspaceId(previous);
      // На откате кэш всё равно чистим: часть отменённых запросов могла успеть
      // записать ответ, полученный уже с новым заголовком.
      queryClient.clear();
      set({ isSwitchingWorkspace: false });
      // Отказ обычно значит, что членство отозвали: список надо перечитать,
      // иначе недоступный workspace останется в выпадающем списке и будет
      // предлагаться снова и снова.
      void get().fetchWorkspaces();
      throw err;
    }
  },

  createWorkspace: async (name: string) => {
    const created = await apiPost<{ workspace: WorkspaceOption }, { name: string }>(
      '/auth/workspaces',
      { name },
    );
    // Новое пространство сразу становится активным и попадает в переключатель.
    await get().switchWorkspace(created.workspace.id);
    await get().fetchWorkspaces();
    return created.workspace;
  },

  clear: () => {
    // Выбор workspace принадлежит вышедшему пользователю: оставить его —
    // значит отправить чужой `X-Workspace-Id` при следующем входе и получить 403.
    writeActiveWorkspaceId(null);
    // Кэш react-query — тоже его: выход идёт SPA-навигацией без перезагрузки,
    // и следующий вошедший в этой вкладке получил бы чужие списки из кэша.
    queryClient.clear();
    set({
      user: null,
      scope: null,
      workspaceSettings: null,
      billing: null,
      platformRole: null,
      workspaces: [],
      error: null,
    });
  },
}));

export function useCurrentUser(): AuthUser | null {
  return useScopeStore((state) => state.user);
}

export function useCurrentScope(): Scope | null {
  return useScopeStore((state) => state.scope);
}

export function useCurrentWorkspaceSettings(): WorkspaceSettingsState | null {
  return useScopeStore((state) => state.workspaceSettings);
}

/**
 * Billing snapshot from the last `/auth/me` (ADR-029). `null` when billing was
 * not shipped by the server — consumers must fail open in that case.
 */
export function useCurrentBilling(): BillingEntitlements | null {
  return useScopeStore((state) => state.billing);
}

export interface BillingAccessState {
  isBillable: boolean;
  plan: string;
  runCenter: {
    allowed: boolean;
    limit: number | 'unlimited';
    currentUsage?: number;
  };
  loading: boolean;
}

/**
 * Narrow billing access selector for feature gates. Fail-open contract
 * (ADR-029): when the server did not return a billing payload the feature is
 * treated as allowed, so pre-billing responses never block existing screens.
 */
export function useBillingAccess(): BillingAccessState {
  const billing = useScopeStore((state) => state.billing);
  const loading = useScopeStore((state) => state.isLoading);
  const runCenterEntitlement = billing?.entitlements['run-center:view'];

  return {
    isBillable: billing?.isBillable ?? false,
    plan: billing?.plan ?? '',
    runCenter: {
      // Missing entitlement (or no billing payload at all) → fail open.
      allowed: runCenterEntitlement?.allowed ?? true,
      limit: runCenterEntitlement?.limit ?? 'unlimited',
      ...(runCenterEntitlement?.currentUsage === undefined
        ? {}
        : { currentUsage: runCenterEntitlement.currentUsage }),
    },
    loading,
  };
}

export function useAvailableWorkspaces(): WorkspaceOption[] {
  return useScopeStore((state) => state.workspaces);
}

export function useWorkspaceSwitching(): boolean {
  return useScopeStore((state) => state.isSwitchingWorkspace);
}

export function useScopeLoading(): boolean {
  return useScopeStore((state) => state.isLoading);
}

/**
 * Platform admin gate for /admin routes and admin-only UI (nav section).
 * Fail-closed: unknown role (null, pre-admin server) or 'user' → false.
 */
export function useIsPlatformAdmin(): boolean {
  return useScopeStore((state) => state.platformRole === 'admin');
}

export function useScopeError(): Error | null {
  return useScopeStore((state) => state.error);
}
